"use client";

import { useEffect, useState } from "react";
import { openWhatsApp, whatsappMessages } from "@/utils/whatsapp";
import { trackWhatsAppClick } from "@/utils/gtm";

function WhatsAppIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function FloatingWhatsApp() {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasOpened = sessionStorage.getItem("whatsapp_opened") === "true";
    if (hasOpened) {
      setShowBadge(false);
      return;
    }

    setShowBadge(true);
    const timer = window.setTimeout(() => setShowBadge(false), 30000);
    return () => window.clearTimeout(timer);
  }, []);

  const panelVisible = isOpen || isHovered;

  useEffect(() => {
    if (!panelVisible) return;

    setCurrentTime(
      new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date())
    );
  }, [panelVisible]);

  const openPanel = () => {
    setIsOpen((prev) => !prev);
    if (typeof window !== "undefined") {
      sessionStorage.setItem("whatsapp_opened", "true");
    }
    setShowBadge(false);
  };

  const startChat = () => {
    openWhatsApp(whatsappMessages.default);
    trackWhatsAppClick("floating_button", "default");
    if (typeof window !== "undefined") {
      sessionStorage.setItem("whatsapp_opened", "true");
    }
    setShowBadge(false);
  };

  return (
    <div
      className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`absolute bottom-16 right-0 w-[280px] md:w-[300px] rounded-2xl overflow-hidden shadow-2xl border border-gray-200 transition-all duration-300 ease-in-out origin-bottom-right ${
          panelVisible
            ? "translate-y-0 opacity-100 pointer-events-auto"
            : "translate-y-4 opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-[#075E54] text-white px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <span className="relative mt-1 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
              </span>
              <div>
                <p className="font-semibold leading-tight">Seasoft Classes</p>
                <p className="text-xs text-green-100 mt-0.5">Typically replies in a few minutes</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-white/90 hover:text-white text-sm"
              aria-label="Close WhatsApp panel"
            >
              X
            </button>
          </div>
        </div>

        <div className="bg-[#ECE5DD] px-4 py-4">
          <div className="bg-white rounded-2xl p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#25D366] text-white flex items-center justify-center text-xs font-bold">
                SC
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                <span className="mr-1">👋</span>
                Hi there! How can we help you today? Click the button below to chat with us on WhatsApp - we typically respond within minutes.
              </p>
            </div>
            <p className="text-[11px] text-gray-400 mt-2 text-right">{currentTime}</p>
          </div>
        </div>

        <div className="bg-white p-3 border-t border-gray-100">
          <button
            type="button"
            onClick={startChat}
            className="w-full bg-[#25D366] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2 hover:bg-[#1ea855] transition-colors"
          >
            <WhatsAppIcon className="w-5 h-5" />
            <span>Start Chat on WhatsApp</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={openPanel}
        aria-label="Open WhatsApp chat"
        className="relative w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(37,211,102,0.4)] hover:scale-110 hover:shadow-[0_8px_20px_rgba(37,211,102,0.5)] transition-all duration-300 ease-in-out whatsapp-pulse"
      >
        <WhatsAppIcon />
        {showBadge ? (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
            1
          </span>
        ) : null}
      </button>
    </div>
  );
}

